import { Component, Injector, OnInit, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NameValuePair } from '@shared/utils/validation/name-value-pair';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AppConsts } from '@shared/AppConsts';
import { AppNavigationService } from '../app-navigation.service';
import { Router } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';

@Component({
    selector: 'search-bar',
    templateUrl: './menu-search-bar.component.html',
    styleUrls: ['./menu-search-bar.component.css'],
})
export class MenuSearchBarComponent extends AppComponentBase implements OnInit {
    allMenuItems: any[];

    searchMenuResults: NameValuePair[];

    isMenuSearchActive = false;

    selected: string = '';

    // ── Voice search (Groq Whisper for the accurate final transcript; the browser's
    // Web Speech API, when present, shows live words while you talk — Level 2 feel) ──
    voiceSupported = false;   // getUserMedia + MediaRecorder available
    voiceConfigured = false;  // server has a Groq key (App:Ai:ApiKey)
    liveSupported = false;    // Web Speech API available for live interim preview
    recording = false;
    transcribing = false;
    voiceError = '';
    voiceResults: NameValuePair[] = [];

    private mediaStream: any = null;
    private mediaRecorder: any = null;
    private audioChunks: any[] = [];
    private recognition: any = null;
    private readonly appServiceBase = AppConsts.remoteServiceBaseUrl + '/api/services/app/';

    constructor(
        injector: Injector,
        private _appNavigationService: AppNavigationService,
        private router: Router,
        private http: HttpClient,
        private zone: NgZone
    ) {
        super(injector);
        this.initializeMenuSearch();
    }

    ngOnInit(): void {
        const nav: any = window.navigator;
        const win: any = window;
        this.voiceSupported = !!(nav && nav.mediaDevices && nav.mediaDevices.getUserMedia && win.MediaRecorder);
        this.liveSupported = !!(win.SpeechRecognition || win.webkitSpeechRecognition);

        // Only offer the mic if the server can actually transcribe (Groq key present).
        if (this.voiceSupported) {
            this.http.get(this.appServiceBase + 'VoiceSearch/IsConfigured').subscribe(
                (r: any) => { this.voiceConfigured = !!(r && (r.result === true || r === true)); },
                () => { this.voiceConfigured = false; }
            );
        }
    }

    showMenuItem(menuItem): boolean {
        return this._appNavigationService.showMenuItem(menuItem);
    }

    searchMenuItem(event) {
        this.searchMenuResults = this.allMenuItems
            .filter(
                (item) =>
                    item.name.toLowerCase().includes(event.query.toLowerCase()) ||
                    item.route.toLowerCase().includes(event.query.toLowerCase())
            )
            .map(
                (menuItem) =>
                    new NameValuePair({
                        name: menuItem.name,
                        value: menuItem.route,
                    })
            );
    }

    selectMenuItem(event) {
        if (event && event.value) {
            this.router.navigate([event.value]).then((navigated) => {
                this.selected = '';
            });
        }
    }

    // ── Mic control ────────────────────────────────────────────────────────────
    toggleMic(): void {
        if (this.transcribing) { return; }
        if (this.recording) { this.stopRecording(); } else { this.startRecording(); }
    }

    private startRecording(): void {
        this.voiceError = '';
        this.voiceResults = [];
        const nav: any = window.navigator;

        nav.mediaDevices.getUserMedia({ audio: true }).then(
            (stream: any) => {
                this.zone.run(() => {
                    this.mediaStream = stream;
                    this.audioChunks = [];

                    let mr: any;
                    try {
                        mr = new (window as any).MediaRecorder(stream, { mimeType: 'audio/webm' });
                    } catch (e) {
                        try { mr = new (window as any).MediaRecorder(stream); }
                        catch (e2) { this.cleanupStream(); this.voiceError = 'Recording is not supported in this browser.'; return; }
                    }

                    this.mediaRecorder = mr;
                    mr.ondataavailable = (ev: any) => {
                        if (ev && ev.data && ev.data.size > 0) { this.audioChunks.push(ev.data); }
                    };
                    mr.onstop = () => { this.zone.run(() => this.onRecordingStopped()); };
                    mr.start();
                    this.recording = true;

                    this.startLivePreview();
                });
            },
            (err: any) => {
                this.zone.run(() => { this.voiceError = 'Microphone permission was denied.'; });
            }
        );
    }

    private stopRecording(): void {
        this.recording = false;
        this.stopLivePreview();
        if (this.mediaRecorder && this.mediaRecorder.state && this.mediaRecorder.state !== 'inactive') {
            try { this.mediaRecorder.stop(); } catch (e) { this.onRecordingStopped(); }
        } else {
            this.onRecordingStopped();
        }
    }

    private onRecordingStopped(): void {
        this.cleanupStream();
        if (!this.audioChunks || this.audioChunks.length === 0) {
            this.transcribing = false;
            return;
        }

        const blob = new Blob(this.audioChunks, { type: 'audio/webm' });
        this.audioChunks = [];
        this.transcribing = true;

        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result as string; // "data:audio/webm;base64,...."
            this.sendToGroq(dataUrl);
        };
        reader.onerror = () => {
            this.zone.run(() => { this.transcribing = false; this.voiceError = 'Could not read the recording.'; });
        };
        reader.readAsDataURL(blob);
    }

    private sendToGroq(dataUrl: string): void {
        const payload = { audioBase64: dataUrl, mimeType: 'audio/webm', fileName: 'voice.webm', language: '' };
        this.http.post(this.appServiceBase + 'VoiceSearch/Transcribe', payload).subscribe(
            (r: any) => {
                this.transcribing = false;
                const text = (r && r.result && r.result.text) ? r.result.text : '';
                this.applyTranscript(text);
            },
            () => {
                this.transcribing = false;
                this.voiceError = 'Could not transcribe. Please try again.';
            }
        );
    }

    /** Put the recognised text in the box and show the matching menu items. */
    private applyTranscript(text: string): void {
        const t = (text || '').trim();
        if (!t) { this.voiceError = 'Did not catch that. Please try again.'; return; }
        this.selected = t;
        this.runVoiceFilter(t);
        if (this.voiceResults.length === 1) {
            this.goTo(this.voiceResults[0]);
        }
    }

    /** Match the spoken phrase to menu items: whole-phrase substring first, then word overlap. */
    private runVoiceFilter(text: string): void {
        const raw = (text || '').toLowerCase().trim();
        const items = this.allMenuItems || [];

        let matches = items.filter(
            (i) => i.name.toLowerCase().includes(raw) || i.route.toLowerCase().includes(raw)
        );

        if (matches.length === 0) {
            const words = raw.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
            matches = items
                .map((i) => {
                    const name = i.name.toLowerCase();
                    let score = 0;
                    for (const w of words) { if (name.indexOf(w) >= 0) { score++; } }
                    return { item: i, score: score };
                })
                .filter((x) => x.score > 0)
                .sort((a, b) => b.score - a.score)
                .map((x) => x.item);
        }

        this.voiceResults = matches
            .slice(0, 8)
            .map((i) => new NameValuePair({ name: i.name, value: i.route }));
    }

    goTo(pair: NameValuePair): void {
        if (pair && pair.value) {
            this.router.navigate([pair.value]);
            this.selected = '';
            this.voiceResults = [];
            this.voiceError = '';
        }
    }

    // ── Web Speech live preview (best-effort; failure never breaks the Groq path) ──
    private startLivePreview(): void {
        if (!this.liveSupported) { return; }
        try {
            const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            const rec: any = new SR();
            rec.lang = 'en-IN';
            rec.interimResults = true;
            rec.continuous = true;
            rec.onresult = (ev: any) => {
                let interim = '';
                for (let i = ev.resultIndex; i < ev.results.length; i++) {
                    interim += ev.results[i][0].transcript;
                }
                if (interim) { this.zone.run(() => { this.selected = interim.trim(); }); }
            };
            rec.onerror = () => { };
            rec.onend = () => { };
            this.recognition = rec;
            rec.start();
        } catch (e) {
            this.recognition = null;
        }
    }

    private stopLivePreview(): void {
        if (this.recognition) {
            try { this.recognition.stop(); } catch (e) { }
            this.recognition = null;
        }
    }

    private cleanupStream(): void {
        if (this.mediaStream) {
            try {
                const tracks = this.mediaStream.getTracks();
                for (const t of tracks) { t.stop(); }
            } catch (e) { }
            this.mediaStream = null;
        }
        this.mediaRecorder = null;
    }

    private getAllMenuItems() {
        return this._appNavigationService
            .getAllMenuItems()
            .filter((item) => this.showMenuItem(item) && item.route)
            .map((menuItem) => ({
                name: this.l(menuItem.name),
                route: menuItem.route,
            }));
    }

    private initializeMenuSearch() {
        this.isMenuSearchActive = false;
        let themeSettings = this.currentTheme.baseSettings;

        if (themeSettings && themeSettings.menu && themeSettings.menu) {
            this.allMenuItems = this.getAllMenuItems();
            this.isMenuSearchActive = true;
        }
    }
}
